<script>
/**
 * KRX 수신 현황 — HQ 전용.
 *
 * 코스콤에서 전문이 제대로 들어오고 있는지 한 화면에서 본다.
 * 검증 날(10/8) 실제로 쓰는 화면이다. 보는 순서가 곧 진단 순서다.
 *
 *   1. 누락 분  — 비어 있어야 한다. 있으면 그 시간은 영구 손실이다.
 *                 코스콤은 재전송해 주지 않는다.
 *   2. 접속 이력 — REJECTED 가 있으면 허용목록에 빠진 IP 가 peer 에 찍혀 있다.
 *                 "왜 안 들어오지"의 1순위 확인처.
 *   3. 요약      — 파일수 / 파싱 / 업로드가 어긋나면 어디가 막혔는지 보인다.
 *   4. 원장      — 분 단위로 내려가 본다.
 *
 * 데이터가 없는 분에도 빈 파일이 만들어진다. 그래서 "원장에 행이 없다"는 것은
 * 장 마감이 아니라 수신 프로세스가 죽어 있었다는 뜻이다.
 */
import {
  getKrxMeta,
  getReceiveSummary,
  getL0Files,
  getMissingMinutes,
  getConnEvents,
} from '@/api/krxMarket.js'

const EVENT_KINDS = [
  { title: '전체', value: '' },
  { title: '접속 (ACCEPTED)', value: 'ACCEPTED' },
  { title: '거절 (REJECTED)', value: 'REJECTED' },
  { title: '종료 (CLOSED)', value: 'CLOSED' },
  { title: '재기동 (RESPAWN)', value: 'RESPAWN' },
]

export default {
  name: 'KrxReceiveStatusPage',

  data() {
    return {
      EVENT_KINDS,
      serverIds: [],
      search: {
        serverId: '',
        bizDate: '',
        kind: '',
      },
      summary: [],
      missing: { count: 0, minutes: [], graceMinutes: 0 },
      events: { items: [], total: 0, page: 1, size: 50 },
      files: { items: [], total: 0, page: 1, size: 100 },
      fileFilter: { pendingOnly: false, errorOnly: false },
      loading: false,
      errorMessage: '',
    }
  },

  computed: {
    /** 선택한 날짜/서버의 요약 한 줄 */
    todaySummary() {
      return this.summary.find(
        (s) => s.bizDate === this.search.bizDate && s.serverId === this.search.serverId,
      ) || null
    },
    /** 받았고 / 읽었고 / 올렸다 — 셋이 같아야 정상이다. */
    pipelineHealthy() {
      const s = this.todaySummary
      if (!s) return null
      return s.fileCount === s.parsedCount && s.parsedCount === s.uploadedCount
    },
    rejectedCount() {
      return this.events.items.filter((e) => e.kind === 'REJECTED').length
    },
  },

  async mounted() {
    await this.loadMeta()
    await this.refresh()
  },

  methods: {
    async loadMeta() {
      try {
        const res = await getKrxMeta()
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '초기 정보를 가져오지 못했습니다.'
          return
        }
        const data = res.data.data || {}
        this.serverIds = data.serverIds || []
        if (!this.search.serverId) {
          this.search.serverId = this.serverIds[0] || ''
        }
        if (!this.search.bizDate) {
          // 서버가 정한 기본 영업일을 쓴다. 브라우저 시간대로 "오늘"을
          // 계산하면 자정 전후로 하루가 어긋난다.
          this.search.bizDate = data.defaultBizDate || ''
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '초기 정보를 가져오지 못했습니다.'
      }
    },

    async refresh() {
      this.loading = true
      this.errorMessage = ''
      try {
        await Promise.all([
          this.fetchSummary(),
          this.fetchMissing(),
          this.fetchEvents(),
          this.fetchFiles(),
        ])
      } finally {
        this.loading = false
      }
    },

    async fetchSummary() {
      const res = await getReceiveSummary({
        serverId: this.search.serverId || undefined,
      })
      if (res.data?.code === 0) {
        this.summary = res.data.data || []
      }
    },

    async fetchMissing() {
      if (!this.search.serverId) {
        this.missing = { count: 0, minutes: [], graceMinutes: 0 }
        return
      }
      const res = await getMissingMinutes({
        serverId: this.search.serverId,
        bizDate: this.search.bizDate || undefined,
      })
      if (res.data?.code === 0) {
        this.missing = res.data.data || { count: 0, minutes: [], graceMinutes: 0 }
      } else {
        this.missing = { count: 0, minutes: [], graceMinutes: 0 }
      }
    },

    async fetchEvents(page = 1) {
      const res = await getConnEvents({
        serverId: this.search.serverId || undefined,
        kind: this.search.kind || undefined,
        startDate: this.search.bizDate || undefined,
        endDate: this.search.bizDate || undefined,
        page,
        size: this.events.size,
      })
      if (res.data?.code === 0) {
        this.events = { ...res.data.data, size: this.events.size }
      }
    },

    async fetchFiles(page = 1) {
      const res = await getL0Files({
        serverId: this.search.serverId || undefined,
        bizDate: this.search.bizDate || undefined,
        pendingOnly: this.fileFilter.pendingOnly,
        errorOnly: this.fileFilter.errorOnly,
        page,
        size: this.files.size,
      })
      if (res.data?.code === 0) {
        this.files = { ...res.data.data, size: this.files.size }
      }
    },

    /* ── 표시 ── */

    formatNumber(v) {
      if (v === null || v === undefined) return '-'
      return Number(v).toLocaleString('ko-KR')
    },

    formatBytes(v) {
      if (!v) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let n = Number(v)
      let i = 0
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024
        i += 1
      }
      return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
    },

    /** ISO 문자열에서 시:분만. 분 단위 데이터라 초는 의미가 없다. */
    formatTime(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleString('ko-KR', { hour12: false })
    },

    formatMinute(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleTimeString('ko-KR', { hour12: false, hour: '2-digit', minute: '2-digit' })
    },

    kindColor(kind) {
      switch (kind) {
        case 'ACCEPTED': return 'success'
        case 'REJECTED': return 'error'
        case 'CLOSED': return 'warning'
        case 'RESPAWN': return 'info'
        default: return 'grey'
      }
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">KRX 수신 현황</div>
        <div class="text-body-2 text-grey mt-1">코스콤 금현물 전문 수신 상태</div>
      </div>
      <v-spacer />
      <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
        <v-icon start>mdi-refresh</v-icon>새로고침
      </v-btn>
    </div>

    <!-- 검색 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-select
              v-model="search.serverId"
              :items="serverIds"
              label="수신 서버"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.bizDate"
              type="date"
              label="영업일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:170px;"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- 1. 누락 분 — 가장 먼저 본다 -->
    <v-alert
      v-if="missing.count > 0"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
    >
      <div class="font-weight-bold mb-1">수신 공백 {{ formatNumber(missing.count) }}분</div>
      <div class="text-body-2 mb-2">
        이 시간에는 수신 프로세스가 떠 있지 않았습니다. 코스콤은 재전송하지 않으므로
        해당 구간의 시세는 복구할 수 없습니다.
        <span v-if="missing.graceMinutes" class="text-caption">
          (원장 등록 시차를 감안해 최근 {{ missing.graceMinutes }}분은 이미 제외한 결과입니다.)
        </span>
      </div>
      <div class="d-flex flex-wrap ga-1">
        <v-chip
          v-for="m in missing.minutes.slice(0, 60)"
          :key="m"
          size="x-small"
          color="error"
          variant="flat"
        >
          {{ formatMinute(m) }}
        </v-chip>
        <v-chip v-if="missing.minutes.length > 60" size="x-small" variant="text">
          외 {{ formatNumber(missing.minutes.length - 60) }}분
        </v-chip>
      </div>
    </v-alert>
    <v-alert v-else type="success" variant="tonal" rounded="lg" class="mb-4" density="compact">
      수신 공백 없음 — 이 영업일의 모든 분이 원장에 기록되어 있습니다.
      <span v-if="missing.graceMinutes" class="text-caption">
        (파서가 원장에 올리기까지 시차가 있어 최근 {{ missing.graceMinutes }}분은 집계에서 제외합니다.)
      </span>
    </v-alert>

    <!-- 3. 요약 카드 -->
    <v-row class="mb-1">
      <v-col cols="12" sm="6" md="3">
        <v-card rounded="lg" elevation="0" border>
          <v-card-text class="pa-4">
            <div class="text-caption text-grey mb-1">수신 / 파싱 / 업로드</div>
            <div class="text-h6 font-weight-bold">
              <template v-if="todaySummary">
                {{ formatNumber(todaySummary.fileCount) }} /
                {{ formatNumber(todaySummary.parsedCount) }} /
                {{ formatNumber(todaySummary.uploadedCount) }}
              </template>
              <template v-else>-</template>
            </div>
            <div v-if="pipelineHealthy === false" class="text-caption text-warning mt-1">
              세 값이 다릅니다 — 파싱이나 업로드가 밀려 있습니다.
            </div>
            <div v-else-if="pipelineHealthy" class="text-caption text-success mt-1">
              받았고, 읽었고, 올렸습니다.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card rounded="lg" elevation="0" border>
          <v-card-text class="pa-4">
            <div class="text-caption text-grey mb-1">수신 전문</div>
            <div class="text-h6 font-weight-bold">
              {{ todaySummary ? formatNumber(todaySummary.frameCount) : '-' }}
            </div>
            <div class="text-caption text-grey mt-1">
              {{ todaySummary ? formatBytes(todaySummary.byteCount) : '' }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card rounded="lg" elevation="0" border>
          <v-card-text class="pa-4">
            <div class="text-caption text-grey mb-1">파싱 오류</div>
            <div
              class="text-h6 font-weight-bold"
              :class="todaySummary && todaySummary.errorCount > 0 ? 'text-error' : ''"
            >
              {{ todaySummary ? formatNumber(todaySummary.errorCount) : '-' }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card rounded="lg" elevation="0" border>
          <v-card-text class="pa-4">
            <div class="text-caption text-grey mb-1">마지막 수신 분</div>
            <div class="text-h6 font-weight-bold">
              {{ todaySummary ? formatMinute(todaySummary.lastMinuteAt) : '-' }}
            </div>
            <div v-if="todaySummary && todaySummary.sessionOpenAt" class="text-caption text-grey mt-1">
              장 {{ formatMinute(todaySummary.sessionOpenAt) }} ~
              {{ formatMinute(todaySummary.sessionCloseAt) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2. 접속 이력 -->
    <v-card rounded="lg" elevation="0" border class="mb-4">
      <v-card-title class="pa-4 pb-2 d-flex align-center">
        <span class="text-subtitle-2 font-weight-bold">접속 이력</span>
        <v-chip v-if="rejectedCount > 0" color="error" size="x-small" class="ml-2" variant="flat">
          거절 {{ rejectedCount }}건
        </v-chip>
        <v-spacer />
        <v-select
          v-model="search.kind"
          :items="EVENT_KINDS"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width:200px;"
          @update:model-value="fetchEvents(1)"
        />
      </v-card-title>
      <v-divider />
      <v-table density="compact">
        <thead>
          <tr>
            <th style="width:180px;">시각</th>
            <th style="width:120px;">구분</th>
            <th style="width:150px;">상대 IP</th>
            <th style="width:90px;">포트</th>
            <th>내용</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(e, i) in events.items" :key="i">
            <td class="text-caption">{{ formatTime(e.at) }}</td>
            <td>
              <v-chip :color="kindColor(e.kind)" size="x-small" variant="flat">{{ e.kind }}</v-chip>
            </td>
            <td class="text-caption font-weight-medium">{{ e.peer || '-' }}</td>
            <td class="text-caption">{{ e.port || '-' }}</td>
            <td class="text-caption text-grey">{{ e.detail || '' }}</td>
          </tr>
          <tr v-if="events.items.length === 0">
            <td colspan="5" class="text-center text-grey py-6">접속 이력이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <v-divider v-if="events.totalPages > 1" />
      <div v-if="events.totalPages > 1" class="pa-3">
        <v-pagination
          :model-value="events.page"
          :length="events.totalPages"
          density="compact"
          total-visible="7"
          @update:model-value="fetchEvents"
        />
      </div>
    </v-card>

    <!-- 4. 원장 -->
    <v-card rounded="lg" elevation="0" border>
      <v-card-title class="pa-4 pb-2 d-flex align-center flex-wrap">
        <span class="text-subtitle-2 font-weight-bold">분 단위 원장</span>
        <span class="text-caption text-grey ml-2">{{ formatNumber(files.total) }}건</span>
        <v-spacer />
        <v-checkbox
          v-model="fileFilter.pendingOnly"
          label="미파싱만"
          density="compact"
          hide-details
          class="mr-3"
          @update:model-value="fetchFiles(1)"
        />
        <v-checkbox
          v-model="fileFilter.errorOnly"
          label="오류만"
          density="compact"
          hide-details
          @update:model-value="fetchFiles(1)"
        />
      </v-card-title>
      <v-divider />
      <v-table density="compact">
        <thead>
          <tr>
            <th style="width:80px;">분</th>
            <th style="width:160px;">파일</th>
            <th class="text-right" style="width:100px;">크기</th>
            <th class="text-right" style="width:90px;">전문</th>
            <th class="text-right" style="width:80px;">오류</th>
            <th style="width:80px;">장중</th>
            <th style="width:90px;">파싱</th>
            <th style="width:90px;">업로드</th>
            <th style="width:90px;">로컬삭제</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in files.items" :key="f.fileName">
            <td class="text-caption font-weight-medium">{{ formatMinute(f.minuteAt) }}</td>
            <td class="text-caption text-grey">{{ f.fileName }}</td>
            <td class="text-right text-caption">{{ formatBytes(f.fileSize) }}</td>
            <td class="text-right text-caption">{{ formatNumber(f.frameCount) }}</td>
            <td class="text-right text-caption" :class="f.errorCount > 0 ? 'text-error font-weight-bold' : ''">
              {{ formatNumber(f.errorCount) }}
            </td>
            <td>
              <v-icon v-if="f.withinSession" size="small" color="success">mdi-circle-small</v-icon>
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <td>
              <v-icon v-if="f.parsedAt" size="small" color="success">mdi-check</v-icon>
              <span v-else class="text-caption text-grey">대기</span>
            </td>
            <td>
              <v-icon v-if="f.uploadedAt" size="small" color="success">mdi-cloud-check-outline</v-icon>
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <td>
              <v-icon v-if="f.deletedAt" size="small" color="grey">mdi-delete-outline</v-icon>
              <span v-else class="text-caption text-grey">-</span>
            </td>
          </tr>
          <tr v-if="files.items.length === 0">
            <td colspan="9" class="text-center text-grey py-6">
              이 영업일의 원장이 없습니다. 수신 프로세스가 떠 있는지 확인하세요.
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-divider v-if="files.totalPages > 1" />
      <div v-if="files.totalPages > 1" class="pa-3">
        <v-pagination
          :model-value="files.page"
          :length="files.totalPages"
          density="compact"
          total-visible="7"
          @update:model-value="fetchFiles"
        />
      </div>
    </v-card>
  </div>
</template>
